import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  fetchSubscriptionsV2 as fetchSubscriptionsV2Api,
  fetchSubscriptionSummary as fetchSubscriptionSummaryApi,
  fetchSubscriptionFilters as fetchSubscriptionFiltersApi,
  deleteSubscription as deleteSubscriptionApi,
  toggleSubscriptionStatus as toggleSubscriptionStatusApi,
  cloneSubscription as cloneSubscriptionApi,
} from "@/helpers/apis";
import { QUERY_OPTIONS } from "@/pages/subscriptions/services/queryConfig";
import { SubscriptionStatus } from "@/pages/subscriptions/constants";
import type { SubscriptionListFilters, SubscriptionSummaryFilters } from "../types";

// Fetch paginated subscriptions list
// Zod validation is handled by api.get in apis.ts (via patchWithZodSchema)
export const useSubscriptionsV2 = (filters: SubscriptionListFilters = {}) => {
  const params = { page: 1, size: 100, ...filters };
  return useQuery({
    queryKey: ["subscriptions-v2", params],
    queryFn: () => fetchSubscriptionsV2Api(params),
    ...QUERY_OPTIONS.list,
  });
};

// Fetch subscription summary metrics
export const useSubscriptionSummary = (filters: SubscriptionSummaryFilters = {}) => {
  return useQuery({
    queryKey: ["subscription-summary", filters],
    queryFn: () => fetchSubscriptionSummaryApi(filters),
    ...QUERY_OPTIONS.list,
  });
};

// Delete subscription using global API
export const useDeleteSubscription = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteSubscriptionApi(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["subscriptions-v2"] });
      await queryClient.invalidateQueries({ queryKey: ["subscription-summary"] });
    },
  });
};

// Toggle subscription status (active/inactive) using global API
export const useToggleSubscriptionStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: SubscriptionStatus }) =>
      toggleSubscriptionStatusApi(id, status),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: ["subscriptions-v2"] });
      queryClient.invalidateQueries({ queryKey: ["subscription-v2", id] });
    },
  });
};

// Clone subscription
export const useCloneSubscription = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => cloneSubscriptionApi(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["subscriptions-v2"] });
      await queryClient.invalidateQueries({ queryKey: ["subscription-summary"] });
    },
  });
};

// Filter options for subscription list
// Zod schema transforms raw values to FilterOption[] via patchWithZodSchema
export const useSubscriptionFilters = () => {
  return useQuery({
    queryKey: ["subscription-filters"],
    queryFn: fetchSubscriptionFiltersApi,
    ...QUERY_OPTIONS.reference,
  });
};
